import './comments.scss';

import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { arrayOf, bool, func, number, oneOf } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import ErrorComponent from '../generic/Error';
import Loading from '../generic/Loading';
import { campaignShape } from '../prop-types';
import api from '../REST/api';
import { initializeDiscussion, poll } from './actions';
import Comments from './Comments.container';
import {
  collectChangeRequests,
  collectMembers,
  nest,
  removeChangeRequests,
} from './transform-comment-helpers';

export const MARK_READ_DELAY = 3; // in seconds
export const POLLING_INTERVAL = 5; // in seconds

function getLastPoll() {
  const justNow = new Date();
  justNow.setSeconds(justNow.getUTCSeconds() - POLLING_INTERVAL);
  return justNow.toISOString();
}

class CommentState extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
    };
  }

  componentDidMount() {
    const { objectId, initialized, onInitialize, contentType } = this.props;

    if (initialized) {
      this.poll();
      this.startPolling();
      this.markAsRead();
      return;
    }

    Promise.all([api.getComments({ contentType, objectId }), api.getUsers()])
      .then(([commentsResponse, usersResponse]) =>
        onInitialize({
          comments: nest(removeChangeRequests(commentsResponse)),
          usersById: keyBy(usersResponse, 'id'),
          teamsByName: collectMembers(usersResponse),
          context: { contentType, objectId },
          changeRequestsByCommentsId: collectChangeRequests(commentsResponse),
        }),
      )
      .then(() => this.startPolling())
      .then(() => this.markAsRead())
      .catch(error => this.setState({ error }));
  }

  componentWillUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    if (this.markAsReadId) {
      clearTimeout(this.markAsReadId);
    }
  }

  startPolling() {
    this.intervalId = setInterval(
      this.poll.bind(this),
      POLLING_INTERVAL * 1000,
    );
  }

  markAsRead() {
    const { objectId, contentType } = this.props;

    this.markAsReadId = setTimeout(
      api.acknowledgeComments,
      MARK_READ_DELAY * 1000,
      { contentType, objectId },
    );
  }

  poll() {
    this.props
      .onPoll(getLastPoll())
      .then(() => this.setState({ error: null }))
      .then(() => this.markAsRead())
      .catch(error => this.setState({ error }));
  }

  render() {
    const { initialized, preferableMentions, campaign } = this.props;
    const { error } = this.state;
    if (error) {
      return <ErrorComponent error={error} />;
    }

    if (initialized) {
      return (
        <Comments preferableMentions={preferableMentions} campaign={campaign} />
      );
    }

    return <Loading />;
  }
}

CommentState.propTypes = {
  contentType: oneOf(['opportunity', 'zms_am_merchant']).isRequired,
  objectId: number.isRequired,
  preferableMentions: arrayOf(number),
  initialized: bool.isRequired,
  onInitialize: func.isRequired,
  onPoll: func.isRequired,
  campaign: campaignShape.isRequired,
};

CommentState.defaultProps = {
  preferableMentions: [],
};

export default connect(
  (state, props) => {
    const context = get(state, ['discussion', 'context'], {});
    const campaignEditor = get(state, ['campaignEditor'], {});
    return {
      initialized:
        context.contentType === props.contentType &&
        context.objectId === props.objectId,
      campaign: campaignEditor?.campaign,
    };
  },
  { onInitialize: initializeDiscussion, onPoll: poll },
)(CommentState);
